import { useEffect, useState } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FileUploader } from '@/components/serge/FileUploader'
import DocumentHistory, { type DocumentHistoryItem } from '@/components/serge-workspace/DocumentHistory'
import { uploadSergeDocument, getSergeDocuments } from '@/services/sergeApi'

interface SergeWorkspaceContext {
  project: { id: number; name: string } | null
  context: Record<string, unknown> | null
  stepsStatus: Record<string, { status: string; isAccessible: boolean }>
  currentStep: string
  prospects: unknown[]
  groups: unknown[]
  refreshProject: () => Promise<void>
}

interface SergeDocument {
  id: number
  filename: string
  originalFilename: string
  mimeType: string
  type: string
  extractedContent: string | null
  uploadedAt: string
}

export default function SergeUploadPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const outletContext = useOutletContext<SergeWorkspaceContext | null>()
  const refreshProject = outletContext?.refreshProject || (async () => {})
  
  const [documents, setDocuments] = useState<SergeDocument[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Charger les documents au montage
  useEffect(() => {
    if (projectId) {
      loadDocuments()
    }
  }, [projectId])

  const loadDocuments = async () => {
    if (!projectId) return
    try {
      const data = await getSergeDocuments(Number(projectId))
      const docList = Array.isArray(data) ? data : data['hydra:member'] || data.documents || []
      setDocuments(docList)
    } catch (err) {
      console.error('Error loading documents:', err)
    }
  }

  const handleUpload = async (files: File[]) => {
    if (!projectId) return

    setIsUploading(true)
    let hasFailure = false

    // Upload chaque fichier
    for (const file of files) {
      try {
        await uploadSergeDocument(Number(projectId), file)
        toast.success(`Fichier envoyé: ${file.name}`)
      } catch {
        toast.error(`Erreur d'upload: ${file.name}`)
        hasFailure = true
      }
    }

    await loadDocuments()
    await refreshProject()
    setIsUploading(false)

    if (hasFailure) {
      throw new Error('upload_failed')
    }
  }

  // Transformer les documents en format DocumentHistory
  const historyItems: DocumentHistoryItem[] = documents.map((doc) => ({
    id: String(doc.id),
    title: doc.originalFilename,
    status: doc.extractedContent ? 'ready' : 'processing',
    detail: doc.extractedContent ? 'Contenu extrait' : 'Extraction en cours...',
    updatedAt: new Date(doc.uploadedAt).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  }))

  const hasReadyDocument = documents.some((doc) => !!doc.extractedContent)
  const hasProcessingDocument = documents.some((doc) => !doc.extractedContent)

  return (
    <div className="grid grid-cols-12 gap-4">
      <section className="col-span-12 xl:col-span-8 space-y-4">
        <div className="border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold text-foreground">Déposer vos documents</h2>
          <p className="text-sm text-foreground-secondary mt-2">
            SERGE analyse vos fichiers et prépare les livrables automatiquement.
          </p>
          <FileUploader
            onUpload={handleUpload}
            autoUpload
            className="mt-6"
            disabled={isUploading}
          />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {hasReadyDocument ? (
              <button
                type="button"
                onClick={() => navigate(`/dashboard/serge-projects/${projectId}/analyse`)}
                className="px-4 py-2 text-sm font-mono border border-primary text-primary hover:bg-primary/10 transition"
              >
                Passer à l'analyse
              </button>
            ) : (
              <span className="text-xs font-mono text-foreground-muted">
                Ajoutez au moins un document analysé pour continuer.
              </span>
            )}
            {hasProcessingDocument && (
              <span className="text-xs font-mono text-foreground-muted">
                Extraction en cours... rafraîchissez l'historique si besoin.
              </span>
            )}
          </div>
        </div>
        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Comment ça fonctionne
          </h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { title: 'Déposer', text: 'Import rapide des cahiers des charges.' },
              { title: 'Analyser', text: 'Extraction sémantique et normalisation.' },
              { title: 'Valider', text: 'Livrables prêts pour édition et export.' },
            ].map((step, index) => (
              <div key={step.title} className="border border-border bg-bg p-4">
                <div className="w-8 h-8 border border-primary text-primary flex items-center justify-center font-mono text-sm">
                  {index + 1}
                </div>
                <p className="text-sm font-semibold text-foreground mt-3">{step.title}</p>
                <p className="text-xs text-foreground-secondary mt-2">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="col-span-12 xl:col-span-4">
        <DocumentHistory
          items={historyItems}
          onRefresh={loadDocuments}
        />
      </section>
    </div>
  )
}
